import React from 'react';
import { createPortal } from 'react-dom';
import { WebcamFocusControl } from './WebcamFocusControl';
import { clientPointToLayoutPoint, clientRectToLayoutRect } from '../../utils/pageZoom';

export type WebcamDrawTool = 'select' | 'draw' | 'eraser';

export type WebcamDrawStroke = {
  id: string;
  color: string;
  width: number;
  points: Array<{ x: number; y: number }>;
};

/** 웹캠 화면 확대·축소·상하/좌우반전 (웹캠 모드에서만 전달 — 칠판 판서에는 없음) */
export type WebcamViewControls = {
  zoom: number;
  flipY: boolean;
  flipX: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onToggleFlipY: () => void;
  onToggleFlipX: () => void;
  /** 카메라 영상 트랙(초점 조절용). 없으면 초점 버튼을 숨김 */
  getVideoTrack?: () => MediaStreamTrack | null;
};

type WebcamDrawContextValue = {
  activeTool: WebcamDrawTool;
  setActiveTool: (tool: WebcamDrawTool) => void;
  color: string;
  setColor: (color: string) => void;
  colorMenuOpen: boolean;
  setColorMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  colorMenuRef: React.RefObject<HTMLDivElement | null>;
  strokes: WebcamDrawStroke[];
  onStrokesChange: (next: WebcamDrawStroke[]) => void;
  layerRef: React.RefObject<HTMLDivElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  draftStrokeRef: React.MutableRefObject<WebcamDrawStroke | null>;
  drawingRef: React.MutableRefObject<boolean>;
  redraw: () => void;
  trashHoldProgress: number;
  startTrashHold: () => void;
  /** 짧게 떼면 true 반환(확인 미니모달), 길게 완료·취소는 false */
  endTrashHold: (openConfirmIfShort: boolean) => boolean;
  clearAllStrokes: () => void;
  downloadHoldProgress: number;
  startDownloadHold: () => void;
  cancelDownloadHold: () => void;
  cursorPos: { x: number; y: number } | null;
  setCursorPos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  normFromEvent: (e: React.PointerEvent | PointerEvent) => { x: number; y: number } | null;
  eraseAt: (nx: number, ny: number) => void;
  onDownloadSnapshot: ((layerEl: HTMLDivElement | null) => void | Promise<void>) | null;
  downloadBusy: boolean;
  requestDownload: () => void;
  webcamView: WebcamViewControls | null;
};

const WebcamDrawContext = React.createContext<WebcamDrawContextValue | null>(null);

function useWebcamDraw() {
  const ctx = React.useContext(WebcamDrawContext);
  if (!ctx) throw new Error('ProgramSlideWebcamDraw requires ProgramSlideWebcamDrawRoot');
  return ctx;
}

export type ProgramSlideWebcamDrawRootProps = {
  strokes: WebcamDrawStroke[];
  onStrokesChange: (next: WebcamDrawStroke[]) => void;
  onDownloadSnapshot?: (layerEl: HTMLDivElement | null) => void | Promise<void>;
  webcamView?: WebcamViewControls;
  children: React.ReactNode;
};

const BRUSH_WIDTH = 5;
const ERASER_RADIUS = 18;
const CURSOR_DIAMETER = 36;
const HOLD_ACTION_MS = 850;
/** 5열×2행: 흰·빨·핑크·노·초 / 검·회·파·보·형광 */
const COLOR_PRESETS: readonly string[] = [
  '#ffffff',
  '#ff3b30',
  '#ff2d95',
  '#ffcc00',
  '#34c759',
  '#000000',
  '#8e8e93',
  '#007aff',
  '#a855f7',
  '#ccff00',
];

function genStrokeId() {
  return `wd-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function distPointToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function strokeHitTest(stroke: WebcamDrawStroke, nx: number, ny: number, radiusNorm: number): boolean {
  const pts = stroke.points;
  if (pts.length === 0) return false;
  if (pts.length === 1) return Math.hypot(pts[0]!.x - nx, pts[0]!.y - ny) <= radiusNorm;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    if (distPointToSegment(nx, ny, a.x, a.y, b.x, b.y) <= radiusNorm) return true;
  }
  return false;
}

function paintStrokes(ctx: CanvasRenderingContext2D, strokes: WebcamDrawStroke[], w: number, h: number) {
  ctx.clearRect(0, 0, w, h);
  for (const stroke of strokes) {
    if (stroke.points.length < 2) continue;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    const first = stroke.points[0]!;
    ctx.moveTo(first.x * w, first.y * h);
    for (let i = 1; i < stroke.points.length; i += 1) {
      const p = stroke.points[i]!;
      ctx.lineTo(p.x * w, p.y * h);
    }
    ctx.stroke();
  }
}

export const ProgramSlideWebcamDrawRoot: React.FC<ProgramSlideWebcamDrawRootProps> = ({
  strokes,
  onStrokesChange,
  onDownloadSnapshot,
  webcamView,
  children,
}) => {
  const [downloadBusy, setDownloadBusy] = React.useState(false);
  const [activeTool, setActiveTool] = React.useState<WebcamDrawTool>('select');
  const [color, setColor] = React.useState('#ff316b');
  const [colorMenuOpen, setColorMenuOpen] = React.useState(false);
  const [cursorPos, setCursorPos] = React.useState<{ x: number; y: number } | null>(null);
  const [trashHoldProgress, setTrashHoldProgress] = React.useState(0);
  const [downloadHoldProgress, setDownloadHoldProgress] = React.useState(0);

  const layerRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const colorMenuRef = React.useRef<HTMLDivElement>(null);
  const draftStrokeRef = React.useRef<WebcamDrawStroke | null>(null);
  const drawingRef = React.useRef(false);
  const trashHoldRafRef = React.useRef<number | null>(null);
  const trashHoldStartRef = React.useRef(0);
  const trashHoldCommittedRef = React.useRef(false);
  const downloadHoldRafRef = React.useRef<number | null>(null);
  const downloadHoldStartRef = React.useRef(0);

  const redraw = React.useCallback(() => {
    const canvas = canvasRef.current;
    const layer = layerRef.current;
    if (!canvas || !layer) return;
    const rect = layer.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const all = draftStrokeRef.current ? [...strokes, draftStrokeRef.current] : strokes;
    paintStrokes(ctx, all, w, h);
  }, [strokes]);

  React.useLayoutEffect(() => {
    redraw();
  }, [redraw]);

  React.useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(layer);
    return () => ro.disconnect();
  }, [redraw]);

  React.useEffect(() => {
    if (!colorMenuOpen) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const root = colorMenuRef.current;
      if (!root || root.contains(e.target as Node)) return;
      setColorMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [colorMenuOpen]);

  const normFromEvent = React.useCallback((e: React.PointerEvent | PointerEvent) => {
    const layer = layerRef.current;
    if (!layer) return null;
    const rect = layer.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
  }, []);

  const eraseAt = React.useCallback(
    (nx: number, ny: number) => {
      const radiusNorm = ERASER_RADIUS / Math.max(layerRef.current?.getBoundingClientRect().width ?? 1, 1);
      const next = strokes.filter((s) => !strokeHitTest(s, nx, ny, radiusNorm));
      if (next.length !== strokes.length) onStrokesChange(next);
    },
    [strokes, onStrokesChange]
  );

  const clearAllStrokes = React.useCallback(() => {
    onStrokesChange([]);
    draftStrokeRef.current = null;
    redraw();
  }, [onStrokesChange, redraw]);

  const cancelTrashHold = React.useCallback(() => {
    if (trashHoldRafRef.current != null) {
      cancelAnimationFrame(trashHoldRafRef.current);
      trashHoldRafRef.current = null;
    }
    trashHoldStartRef.current = 0;
    setTrashHoldProgress(0);
  }, []);

  const cancelDownloadHold = React.useCallback(() => {
    if (downloadHoldRafRef.current != null) {
      cancelAnimationFrame(downloadHoldRafRef.current);
      downloadHoldRafRef.current = null;
    }
    downloadHoldStartRef.current = 0;
    setDownloadHoldProgress(0);
  }, []);

  const startTrashHold = React.useCallback(() => {
    cancelTrashHold();
    cancelDownloadHold();
    trashHoldCommittedRef.current = false;
    trashHoldStartRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - trashHoldStartRef.current;
      const progress = Math.min(1, elapsed / HOLD_ACTION_MS);
      setTrashHoldProgress(progress);
      if (progress >= 1) {
        trashHoldCommittedRef.current = true;
        clearAllStrokes();
        cancelTrashHold();
        return;
      }
      trashHoldRafRef.current = requestAnimationFrame(tick);
    };
    trashHoldRafRef.current = requestAnimationFrame(tick);
  }, [cancelTrashHold, cancelDownloadHold, clearAllStrokes]);

  const endTrashHold = React.useCallback(
    (openConfirmIfShort: boolean) => {
      const hadPending = trashHoldRafRef.current != null;
      const committed = trashHoldCommittedRef.current;
      cancelTrashHold();
      return openConfirmIfShort && hadPending && !committed;
    },
    [cancelTrashHold]
  );

  const requestDownload = React.useCallback(() => {
    if (!onDownloadSnapshot || downloadBusy) return;
    setDownloadBusy(true);
    void Promise.resolve(onDownloadSnapshot(layerRef.current)).finally(() => {
      setDownloadBusy(false);
    });
  }, [onDownloadSnapshot, downloadBusy]);

  const startDownloadHold = React.useCallback(() => {
    if (!onDownloadSnapshot || downloadBusy) return;
    cancelDownloadHold();
    cancelTrashHold();
    downloadHoldStartRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - downloadHoldStartRef.current;
      const progress = Math.min(1, elapsed / HOLD_ACTION_MS);
      setDownloadHoldProgress(progress);
      if (progress >= 1) {
        cancelDownloadHold();
        requestDownload();
        return;
      }
      downloadHoldRafRef.current = requestAnimationFrame(tick);
    };
    downloadHoldRafRef.current = requestAnimationFrame(tick);
  }, [cancelDownloadHold, cancelTrashHold, onDownloadSnapshot, downloadBusy, requestDownload]);

  React.useEffect(
    () => () => {
      cancelTrashHold();
      cancelDownloadHold();
    },
    [cancelTrashHold, cancelDownloadHold]
  );

  const value = React.useMemo<WebcamDrawContextValue>(
    () => ({
      activeTool,
      setActiveTool,
      color,
      setColor,
      colorMenuOpen,
      setColorMenuOpen,
      colorMenuRef,
      strokes,
      onStrokesChange,
      layerRef,
      canvasRef,
      draftStrokeRef,
      drawingRef,
      redraw,
      trashHoldProgress,
      startTrashHold,
      endTrashHold,
      clearAllStrokes,
      downloadHoldProgress,
      startDownloadHold,
      cancelDownloadHold,
      cursorPos,
      setCursorPos,
      normFromEvent,
      eraseAt,
      onDownloadSnapshot: onDownloadSnapshot ?? null,
      downloadBusy,
      requestDownload,
      webcamView: webcamView ?? null,
    }),
    [
      activeTool,
      color,
      colorMenuOpen,
      strokes,
      onStrokesChange,
      redraw,
      trashHoldProgress,
      startTrashHold,
      endTrashHold,
      clearAllStrokes,
      downloadHoldProgress,
      startDownloadHold,
      cancelDownloadHold,
      cursorPos,
      normFromEvent,
      eraseAt,
      onDownloadSnapshot,
      downloadBusy,
      requestDownload,
      webcamView,
    ]
  );

  return <WebcamDrawContext.Provider value={value}>{children}</WebcamDrawContext.Provider>;
};

export const ProgramSlideWebcamDrawCanvas: React.FC = () => {
  const {
    activeTool,
    color,
    layerRef,
    canvasRef,
    draftStrokeRef,
    drawingRef,
    redraw,
    strokes,
    onStrokesChange,
    cursorPos,
    setCursorPos,
    normFromEvent,
    eraseAt,
  } = useWebcamDraw();

  const isDrawingTool = activeTool === 'draw' || activeTool === 'eraser';

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isDrawingTool) return;
    if (e.button !== 0) return;
    const p = normFromEvent(e);
    if (!p) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drawingRef.current = true;
    if (activeTool === 'eraser') {
      eraseAt(p.x, p.y);
      return;
    }
    draftStrokeRef.current = {
      id: genStrokeId(),
      color,
      width: BRUSH_WIDTH,
      points: [p],
    };
    redraw();
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const layer = layerRef.current;
    if (layer && isDrawingTool) {
      const rect = layer.getBoundingClientRect();
      setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    } else if (!drawingRef.current) {
      setCursorPos(null);
    }
    if (!drawingRef.current || !isDrawingTool) return;
    const p = normFromEvent(e);
    if (!p) return;
    e.stopPropagation();
    if (activeTool === 'eraser') {
      eraseAt(p.x, p.y);
      return;
    }
    const draft = draftStrokeRef.current;
    if (!draft) return;
    const last = draft.points[draft.points.length - 1];
    if (last && Math.hypot(last.x - p.x, last.y - p.y) < 0.001) return;
    draftStrokeRef.current = { ...draft, points: [...draft.points, p] };
    redraw();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (activeTool === 'eraser') return;
    const draft = draftStrokeRef.current;
    draftStrokeRef.current = null;
    if (draft && draft.points.length >= 2) {
      onStrokesChange([...strokes, draft]);
    } else {
      redraw();
    }
  };

  const showCursor = isDrawingTool && cursorPos != null;
  const cursorSize = activeTool === 'eraser' ? ERASER_RADIUS * 2 : CURSOR_DIAMETER;

  return (
    <div
      ref={layerRef}
      className={'cp-program-slide-webcam-draw-layer' + (isDrawingTool ? ' is-drawing-active' : ' is-select-pass-through')}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={() => setCursorPos(null)}
    >
      <canvas ref={canvasRef} className="cp-program-slide-webcam-draw-canvas" aria-hidden />
      {showCursor ? (
        <div
          className="cp-program-slide-webcam-draw-cursor"
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            width: cursorSize,
            height: cursorSize,
          }}
          aria-hidden
        />
      ) : null}
    </div>
  );
};

export const ProgramSlideWebcamDrawToolbar: React.FC = () => {
  const {
    activeTool,
    setActiveTool,
    color,
    setColor,
    colorMenuOpen,
    setColorMenuOpen,
    colorMenuRef,
    trashHoldProgress,
    startTrashHold,
    endTrashHold,
    clearAllStrokes,
    downloadHoldProgress,
    startDownloadHold,
    cancelDownloadHold,
    onDownloadSnapshot,
    downloadBusy,
    webcamView,
  } = useWebcamDraw();

  const hostRef = React.useRef<HTMLDivElement>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const [toolbarPos, setToolbarPos] = React.useState<{ left: number; top: number } | null>(null);
  const [draggingToolbar, setDraggingToolbar] = React.useState(false);
  const [trashConfirmOpen, setTrashConfirmOpen] = React.useState(false);
  const trashConfirmTitleId = React.useId();
  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
  } | null>(null);

  const pickTool = (tool: WebcamDrawTool) => {
    setColorMenuOpen(false);
    setActiveTool(tool);
  };

  const clampToolbarPos = React.useCallback((left: number, top: number) => {
    const host = hostRef.current;
    const toolbar = toolbarRef.current;
    if (!host || !toolbar) return { left, top };
    const margin = 8;
    const maxLeft = Math.max(margin, host.clientWidth - toolbar.offsetWidth - margin);
    const maxTop = Math.max(margin, host.clientHeight - toolbar.offsetHeight - margin);
    return {
      left: Math.min(Math.max(left, margin), maxLeft),
      top: Math.min(Math.max(top, margin), maxTop),
    };
  }, []);

  const onDragHandlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setColorMenuOpen(false);
    setTrashConfirmOpen(false);
    const host = hostRef.current;
    const toolbar = toolbarRef.current;
    if (!host || !toolbar) return;
    const hostRect = clientRectToLayoutRect(host.getBoundingClientRect());
    const toolbarRect = clientRectToLayoutRect(toolbar.getBoundingClientRect());
    const originLeft = toolbarRect.left - hostRect.left;
    const originTop = toolbarRect.top - hostRect.top;
    const pointer = clientPointToLayoutPoint(e.clientX, e.clientY);
    setToolbarPos(clampToolbarPos(originLeft, originTop));
    dragRef.current = {
      pointerId: e.pointerId,
      startX: pointer.x,
      startY: pointer.y,
      originLeft,
      originTop,
    };
    setDraggingToolbar(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const onDragHandlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const pointer = clientPointToLayoutPoint(e.clientX, e.clientY);
    const nextLeft = drag.originLeft + (pointer.x - drag.startX);
    const nextTop = drag.originTop + (pointer.y - drag.startY);
    setToolbarPos(clampToolbarPos(nextLeft, nextTop));
  };

  const endToolbarDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    dragRef.current = null;
    setDraggingToolbar(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
  };

  const resetToolbarPosition = () => {
    setToolbarPos(null);
    setDraggingToolbar(false);
    dragRef.current = null;
  };

  React.useEffect(() => {
    if (!trashConfirmOpen) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setTrashConfirmOpen(false);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [trashConfirmOpen]);

  const toolbarStyle: React.CSSProperties | undefined = toolbarPos
    ? {
        left: `${Math.round(toolbarPos.left)}px`,
        top: `${Math.round(toolbarPos.top)}px`,
        bottom: 'auto',
        transform: 'none',
      }
    : undefined;

  return (
    <div className="cp-program-slide-webcam-draw-toolbar-host" ref={hostRef}>
      {colorMenuOpen ? (
        <div
          className="cp-program-slide-webcam-draw-color-backdrop"
          aria-hidden
          onPointerDown={(e) => {
            e.stopPropagation();
            setColorMenuOpen(false);
          }}
        />
      ) : null}
      <div
        ref={toolbarRef}
        className={
          'cp-program-slide-webcam-draw-toolbar' +
          (colorMenuOpen || draggingToolbar ? ' is-toolbar-engaged' : '') +
          (draggingToolbar ? ' is-dragging' : '') +
          (toolbarPos ? ' is-repositioned' : '')
        }
        style={toolbarStyle}
        role="toolbar"
        aria-label="웹캠 그리기 도구"
      >
        <button
          type="button"
          className="cp-program-slide-webcam-draw-tool-btn cp-program-slide-webcam-draw-tool-btn--drag"
          title="끌어서 도구 위치 이동"
          aria-label="그리기 도구 위치 이동"
          onPointerDown={onDragHandlePointerDown}
          onPointerMove={onDragHandlePointerMove}
          onPointerUp={endToolbarDrag}
          onPointerCancel={endToolbarDrag}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path d="M6 8H18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 12H18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 16H18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="cp-program-slide-webcam-draw-color-wrap" ref={colorMenuRef}>
          <button
            type="button"
            className={'cp-program-slide-webcam-draw-tool-btn' + (colorMenuOpen ? ' is-active' : '')}
            title="색상 선택"
            aria-label="색상 선택"
            aria-expanded={colorMenuOpen}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setTrashConfirmOpen(false);
              setColorMenuOpen((v) => !v);
            }}
          >
            <span
              className="cp-program-slide-webcam-draw-color-swatch"
              style={{ ['--webcam-draw-swatch' as string]: color }}
            />
          </button>
          {colorMenuOpen ? (
            <div
              className="cp-program-slide-webcam-draw-color-menu"
              role="dialog"
              aria-label="색상"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="cp-program-slide-webcam-draw-color-presets" role="list" aria-label="기본 색상">
                {COLOR_PRESETS.map((c, i) => (
                  <button
                    key={`${c}-${i}`}
                    type="button"
                    role="listitem"
                    className={'cp-program-slide-webcam-draw-color-preset' + (c === color ? ' is-selected' : '')}
                    style={{ background: c }}
                    aria-label={`색상 ${c}`}
                    aria-pressed={c === color}
                    onClick={() => {
                      setColor(c);
                      pickTool('draw');
                    }}
                  />
                ))}
              </div>
              <div className="cp-program-slide-webcam-draw-color-picker-inline">
                <input
                  id="cp-program-slide-webcam-draw-color"
                  type="color"
                  className="cp-program-slide-webcam-draw-color-picker-input"
                  value={color}
                  onChange={(e) => {
                    setColor(e.target.value);
                    pickTool('draw');
                  }}
                  aria-label="자유 색상 선택"
                />
              </div>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={'cp-program-slide-webcam-draw-tool-btn' + (activeTool === 'select' ? ' is-active' : '')}
          title="선택"
          aria-label="선택"
          aria-pressed={activeTool === 'select'}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setTrashConfirmOpen(false);
            pickTool('select');
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              d="M21.014 16.7971L16.6237 12.4078L20.1796 11.1412L20.2509 11.1131C20.5971 10.9601 20.889 10.706 21.088 10.3841C21.2871 10.0622 21.3841 9.68755 21.3663 9.30947C21.3484 8.93139 21.2165 8.56757 20.988 8.26585C20.7595 7.96412 20.445 7.73862 20.0859 7.61901L5.07745 2.7187C4.74921 2.61321 4.39825 2.60027 4.06314 2.6813C3.72803 2.76232 3.42175 2.93417 3.17796 3.17796C2.93417 3.42175 2.76232 3.72803 2.6813 4.06314C2.60027 4.39825 2.61321 4.74921 2.7187 5.07745L7.61901 20.0849C7.73862 20.444 7.96412 20.7585 8.26585 20.9871C8.56757 21.2156 8.9314 21.3475 9.30947 21.3653C9.68755 21.3832 10.0622 21.2862 10.3841 21.0871C10.706 20.888 10.9601 20.5962 11.1131 20.2499C11.1234 20.2265 11.1328 20.2031 11.1412 20.1787L12.4078 16.6237L16.7981 21.0131C17.1497 21.3646 17.6265 21.562 18.1237 21.562C18.6209 21.562 19.0977 21.3646 19.4493 21.0131L21.014 19.4493C21.1882 19.2752 21.3264 19.0685 21.4207 18.8409C21.5149 18.6134 21.5635 18.3695 21.5635 18.1232C21.5635 17.8769 21.5149 17.6331 21.4207 17.4055C21.3264 17.178 21.1882 16.9713 21.014 16.7971ZM18.1237 19.1568L13.5787 14.6118C13.2271 14.2602 12.7503 14.0626 12.2531 14.0624C12.1354 14.0627 12.0181 14.0737 11.9024 14.0953C11.6025 14.152 11.3209 14.2812 11.0822 14.4716C10.8435 14.6619 10.6549 14.9076 10.5328 15.1874C10.5224 15.2109 10.5131 15.2343 10.5046 15.2587L9.41245 18.3281L5.08963 5.08963L18.3281 9.41151L15.2615 10.5046C15.2371 10.5131 15.2137 10.5224 15.1903 10.5328C14.9101 10.6546 14.6639 10.8431 14.4732 11.0818C14.2824 11.3205 14.1529 11.6021 14.0959 11.9023C14.0389 12.2025 14.0561 12.512 14.146 12.804C14.2359 13.0961 14.3958 13.3617 14.6118 13.5778L19.1578 18.1228L18.1237 19.1568Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <button
          type="button"
          className={'cp-program-slide-webcam-draw-tool-btn' + (activeTool === 'draw' ? ' is-active' : '')}
          title="그리기"
          aria-label="그리기"
          aria-pressed={activeTool === 'draw'}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setTrashConfirmOpen(false);
            pickTool('draw');
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              d="M3 21V17C3 16.2089 3.2346 15.4355 3.67412 14.7777C4.11365 14.1199 4.73836 13.6072 5.46927 13.3045C6.20017 13.0017 7.00444 12.9225 7.78036 13.0769C8.55629 13.2312 9.26902 13.6122 9.82843 14.1716C10.3878 14.731 10.7688 15.4437 10.9231 16.2196C11.0775 16.9956 10.9983 17.7998 10.6955 18.5307C10.3928 19.2616 9.88008 19.8864 9.22228 20.3259C8.56448 20.7654 7.79113 21 7 21H3Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 3C18.1458 3.3904 15.4499 4.54414 13.197 6.33944C10.9441 8.13474 9.21765 10.505 8.19995 13.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 3C20.6096 5.85418 19.4559 8.55002 17.6606 10.8029C15.8653 13.0559 13.4951 14.7823 10.8 15.8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.6 9C12.5431 9.89687 14.1031 11.4568 15 13.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className={'cp-program-slide-webcam-draw-tool-btn' + (activeTool === 'eraser' ? ' is-active' : '')}
          title="지우개"
          aria-label="지우개"
          aria-pressed={activeTool === 'eraser'}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setTrashConfirmOpen(false);
            pickTool('eraser');
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              d="M20.25 19.125H13.2188L21.36 10.9838C21.6038 10.74 21.7972 10.4506 21.9291 10.1321C22.0611 9.81364 22.129 9.47227 22.129 9.12753C22.129 8.78279 22.0611 8.44142 21.9291 8.12292C21.7972 7.80443 21.6038 7.51504 21.36 7.27128L17.4778 3.39284C17.2341 3.14906 16.9447 2.95568 16.6262 2.82374C16.3077 2.69181 15.9663 2.6239 15.6216 2.6239C15.2768 2.6239 14.9355 2.69181 14.617 2.82374C14.2985 2.95568 14.0091 3.14906 13.7653 3.39284L2.64284 14.5144C2.39906 14.7582 2.20568 15.0476 2.07374 15.366C1.94181 15.6845 1.8739 16.0259 1.8739 16.3707C1.8739 16.7154 1.94181 17.0568 2.07374 17.3753C2.20568 17.6938 2.39906 17.9831 2.64284 18.2269L5.46097 21.046C5.56552 21.1504 5.68963 21.2332 5.8262 21.2897C5.96277 21.3461 6.10912 21.3751 6.2569 21.375H20.25C20.5484 21.375 20.8345 21.2565 21.0455 21.0455C21.2565 20.8345 21.375 20.5484 21.375 20.25C21.375 19.9517 21.2565 19.6655 21.0455 19.4545C20.8345 19.2436 20.5484 19.125 20.25 19.125ZM15.3563 4.98378C15.3911 4.94891 15.4325 4.92125 15.478 4.90238C15.5235 4.88351 15.5723 4.8738 15.6216 4.8738C15.6709 4.8738 15.7197 4.88351 15.7652 4.90238C15.8107 4.92125 15.8521 4.94891 15.8869 4.98378L19.7663 8.86315C19.8364 8.93345 19.8758 9.0287 19.8758 9.128C19.8758 9.2273 19.8364 9.32254 19.7663 9.39284L15 14.1563L10.5938 9.75003L15.3563 4.98378ZM6.72284 19.125L4.23378 16.636C4.16365 16.5657 4.12426 16.4704 4.12426 16.3711C4.12426 16.2718 4.16365 16.1766 4.23378 16.1063L9.00003 11.3438L13.4063 15.75L10.0313 19.125H6.72284Z"
              fill="currentColor"
            />
          </svg>
        </button>
        {webcamView ? (
          <>
            <span className="cp-program-slide-webcam-draw-toolbar-sep" aria-hidden />
            <button
              type="button"
              className="cp-program-slide-webcam-draw-tool-btn"
              title="웹캠 확대"
              aria-label="웹캠 확대"
              disabled={!webcamView.canZoomIn}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setTrashConfirmOpen(false);
                webcamView.onZoomIn();
              }}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M15.5 15.5L21 21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M10.5 7.5V13.5M7.5 10.5H13.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              className="cp-program-slide-webcam-draw-tool-btn cp-program-slide-webcam-draw-zoom-label"
              title="웹캠 배율 · 누르면 100%로"
              aria-label={`웹캠 배율 ${Math.round(webcamView.zoom * 100)}%, 누르면 100%로`}
              disabled={webcamView.zoom === 1}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setTrashConfirmOpen(false);
                webcamView.onZoomReset();
              }}
            >
              {Math.round(webcamView.zoom * 100)}%
            </button>
            <button
              type="button"
              className="cp-program-slide-webcam-draw-tool-btn"
              title="웹캠 축소"
              aria-label="웹캠 축소"
              disabled={!webcamView.canZoomOut}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setTrashConfirmOpen(false);
                webcamView.onZoomOut();
              }}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M15.5 15.5L21 21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M7.5 10.5H13.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              className={'cp-program-slide-webcam-draw-tool-btn' + (webcamView.flipY ? ' is-active' : '')}
              title="웹캠 상하반전"
              aria-label="웹캠 상하반전"
              aria-pressed={webcamView.flipY}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setTrashConfirmOpen(false);
                webcamView.onToggleFlipY();
              }}
            >
              <span
                className="cp-program-slide-webcam-draw-flip-icon"
                style={
                  {
                    '--cp-webcam-flip-icon': `url("${process.env.PUBLIC_URL || ''}/Vertical.svg")`,
                  } as React.CSSProperties
                }
                aria-hidden
              />
            </button>
            <button
              type="button"
              className={'cp-program-slide-webcam-draw-tool-btn' + (webcamView.flipX ? ' is-active' : '')}
              title="웹캠 좌우반전"
              aria-label="웹캠 좌우반전"
              aria-pressed={webcamView.flipX}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setTrashConfirmOpen(false);
                webcamView.onToggleFlipX();
              }}
            >
              <span
                className="cp-program-slide-webcam-draw-flip-icon is-horizontal"
                style={
                  {
                    '--cp-webcam-flip-icon': `url("${process.env.PUBLIC_URL || ''}/Vertical.svg")`,
                  } as React.CSSProperties
                }
                aria-hidden
              />
            </button>
            {webcamView.getVideoTrack ? (
              <WebcamFocusControl
                getVideoTrack={webcamView.getVideoTrack}
                onBeforeOpen={() => {
                  setTrashConfirmOpen(false);
                  setColorMenuOpen(false);
                }}
              />
            ) : null}
            <span className="cp-program-slide-webcam-draw-toolbar-sep" aria-hidden />
          </>
        ) : null}
        <button
          type="button"
          className="cp-program-slide-webcam-draw-tool-btn cp-program-slide-webcam-draw-tool-btn--trash"
          title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
          aria-label="그리기 전체 삭제: 짧게 확인, 길게 바로 삭제"
          aria-expanded={trashConfirmOpen}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setTrashConfirmOpen(false);
            startTrashHold();
          }}
          onPointerUp={(e) => {
            e.stopPropagation();
            if (endTrashHold(true)) setTrashConfirmOpen(true);
          }}
          onPointerLeave={(e) => {
            e.stopPropagation();
            endTrashHold(false);
          }}
          onPointerCancel={(e) => {
            e.stopPropagation();
            endTrashHold(false);
          }}
        >
          <span
            className="cp-program-slide-webcam-draw-hold-gauge"
            style={{ ['--hold-progress' as string]: String(trashHoldProgress) }}
            aria-hidden
          />
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            className="cp-program-slide-webcam-draw-trash-icon"
            aria-hidden
          >
            <path d="M3 6H5H21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className="cp-program-slide-webcam-draw-tool-btn cp-program-slide-webcam-draw-tool-btn--reset-pos"
          title="도구 위치 초기화"
          aria-label="그리기 도구 위치 초기화"
          disabled={!toolbarPos}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setTrashConfirmOpen(false);
            resetToolbarPosition();
          }}
        >
          <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden className="cp-program-slide-webcam-draw-reset-icon">
            <path
              d="M4.2002 0.946167C5.93592 0.0188507 7.95245 -0.237956 9.86523 0.224487L9.86426 0.225464C11.7772 0.684391 13.455 1.83097 14.5752 3.44812C15.6955 5.06558 16.1796 7.03957 15.9365 8.99207V8.99402C15.6895 10.946 14.733 12.7392 13.249 14.0311C11.7652 15.3227 9.85761 16.0228 7.89062 15.9989C6.19016 15.9808 4.53904 15.4231 3.17676 14.4052C1.81445 13.3871 0.810707 11.9616 0.311523 10.3358C0.149909 9.80829 0.44635 9.24914 0.973633 9.08679C1.50144 8.92473 2.06135 9.22122 2.22363 9.7489C2.59828 10.9691 3.35158 12.0395 4.37402 12.8036C5.39646 13.5675 6.63591 13.9863 7.91211 13.9999H7.91406C9.39033 14.0181 10.8219 13.4917 11.9355 12.5223C13.0491 11.5529 13.7668 10.2078 13.9521 8.74304C14.1339 7.27945 13.7704 5.80026 12.9307 4.58777C12.0904 3.37477 10.8324 2.51293 9.39746 2.16882H9.39551C7.95956 1.82159 6.44561 2.01463 5.14258 2.71082C4.14902 3.24176 3.33104 4.03734 2.77246 4.99988H5C5.55228 4.99988 6 5.44759 6 5.99988C5.99997 6.55213 5.55226 6.99988 5 6.99988H1C0.447736 6.99988 3.29844e-05 6.55213 0 5.99988V1.99988C0 1.44759 0.447715 0.999878 1 0.999878C1.55228 0.999878 2 1.44759 2 1.99988V2.67761C2.62154 1.98188 3.36371 1.39313 4.2002 0.946167Z"
              fill="currentColor"
            />
          </svg>
        </button>
        {onDownloadSnapshot ? (
          <button
            type="button"
            className="cp-program-slide-webcam-draw-tool-btn cp-program-slide-webcam-draw-tool-btn--download"
            title="길게 눌러 PNG 저장"
            aria-label="길게 눌러 웹캠·그리기 PNG 저장"
            disabled={downloadBusy}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setTrashConfirmOpen(false);
              startDownloadHold();
            }}
            onPointerUp={(e) => {
              e.stopPropagation();
              cancelDownloadHold();
            }}
            onPointerLeave={(e) => {
              e.stopPropagation();
              cancelDownloadHold();
            }}
            onPointerCancel={(e) => {
              e.stopPropagation();
              cancelDownloadHold();
            }}
          >
            <span
              className="cp-program-slide-webcam-draw-hold-gauge"
              style={{ ['--hold-progress' as string]: String(downloadHoldProgress) }}
              aria-hidden
            />
            <svg viewBox="0 0 24 24" width="22" height="22" className="cp-program-slide-webcam-draw-tool-btn-icon" aria-hidden>
              <path d="M12 4v11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path
                d="M8 11l4 4 4-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M5 20h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
      </div>
      {trashConfirmOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport cp-delete-hold-confirm-overlay"
              role="presentation"
              onClick={() => setTrashConfirmOpen(false)}
              onMouseDown={(ev) => ev.stopPropagation()}
              onPointerDown={(ev) => ev.stopPropagation()}
            >
              <div
                className="cp-pin-confirm cp-pin-confirm--warn"
                role="dialog"
                aria-modal="true"
                aria-labelledby={trashConfirmTitleId}
                onClick={(ev) => ev.stopPropagation()}
              >
                <div className="cp-pin-confirm-warn-icon" aria-hidden>
                  !
                </div>
                <p id={trashConfirmTitleId} className="cp-pin-confirm-warn-title">
                  그린 내용을 모두 삭제할까요?
                </p>
                <p className="cp-pin-confirm-warn-desc">삭제하면 되돌릴 수 없습니다.</p>
                <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                    onClick={() => setTrashConfirmOpen(false)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                    onClick={() => {
                      setTrashConfirmOpen(false);
                      clearAllStrokes();
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

/** @deprecated Use ProgramSlideWebcamDrawRoot + Canvas + Toolbar */
const ProgramSlideWebcamDrawOverlay: React.FC<{
  strokes: WebcamDrawStroke[];
  onStrokesChange: (next: WebcamDrawStroke[]) => void;
}> = ({ strokes, onStrokesChange }) => (
  <ProgramSlideWebcamDrawRoot strokes={strokes} onStrokesChange={onStrokesChange}>
    <ProgramSlideWebcamDrawCanvas />
    <ProgramSlideWebcamDrawToolbar />
  </ProgramSlideWebcamDrawRoot>
);

export default ProgramSlideWebcamDrawOverlay;
